import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { useQueries, useQuery } from '@tanstack/react-query'
import { erc20Abi } from 'abitype/abis'
import { formatUnits } from 'viem'
import { usePublicClient } from 'wagmi'

import { active_chain } from '@repo/common/blockchain/config'

import { useListCryptoContracts } from '../../crypto_contracts'
import { amount_to_tokens } from '../../helpers/block_helpers'
import {
  disableKeyWhenMissing,
  type DisableKeyWhenMissingFlag,
} from '../../helpers/utilities'

export const keys = createQueryKeyStore({
  allBalances: {
    wallet: (wallet_address: CryptoAddress | DisableKeyWhenMissingFlag) => ({
      queryKey: [{ wallet_address }],
    }),
    wallets: (
      wallet_addresses: (CryptoAddress | DisableKeyWhenMissingFlag)[],
    ) => ({
      queryKey: [{ wallet_addresses }],
    }),
  },
})

export type TokenBalance = {
  symbol: string
  balance: number
  decimals: number
  token_address: CryptoAddress
}

export type AllBalances = {
  eth: number
  tokens: TokenBalance[]
  address?: CryptoAddress
}

// eslint-disable-next-line complexity
export function useGetWalletBalances(address: CryptoAddress) {
  const client = usePublicClient({ chainId: active_chain.id })
  const {
    data,
    isLoading: isLoadingContracts,
    isError: isErrorContracts,
    error: errorContracts,
  } = useListCryptoContracts()
  const contracts = data?.contracts ?? []

  const enabled = client != null && contracts.length > 0

  const results = useQuery({
    queryKey: keys.allBalances.wallet(disableKeyWhenMissing(address)).queryKey,
    queryFn: !enabled
      ? () => ({ eth: 0, tokens: [] as TokenBalance[], address })
      : async () => {
          // Get ETH balance
          let ethBalance = 0
          try {
            const rawEthBalance = await client.getBalance({ address })
            ethBalance = Number(formatUnits(rawEthBalance, 18)) // ETH has 18 decimals
          } catch (error) {
            console.error('Error fetching ETH balance:', error)
          }

          // Get ERC20 token balances
          const balancePromises = contracts.map(async (contract) => {
            try {
              // Skip contracts without symbols
              if (!contract.symbol) {
                return null
              }

              // Get token decimals
              const decimals = await client.readContract({
                abi: erc20Abi,
                functionName: 'decimals',
                address: contract.address,
              })

              // Get token balance
              const rawBalance = await client.readContract({
                abi: erc20Abi,
                functionName: 'balanceOf',
                args: [address], // if doesn't exist, query is disabled
                address: contract.address,
              })

              // Convert to human-readable format
              const balance = amount_to_tokens(rawBalance, decimals)

              // Only return tokens with non-zero balances
              if (balance <= 0) {
                return null
              }

              return {
                symbol: contract.symbol,
                balance,
                decimals,
                token_address: contract.address,
              }
            } catch (error) {
              console.error(
                `Error fetching balance for ${contract.symbol}:`,
                error,
              )
              return null
            }
          })

          const tokenResults = await Promise.all(balancePromises)

          // Filter out null values (errors or zero balances) and sort by balance descending
          const tokens = tokenResults
            .filter((balance): balance is TokenBalance => balance !== null)
            .sort((a, b) => b.balance - a.balance)

          return {
            eth: ethBalance,
            tokens,
            address,
          }
        },
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  })

  return {
    // ...results,
    isLoading: results.isLoading || isLoadingContracts,
    isError: results.isError || isErrorContracts,
    error: results.error || errorContracts,
    ethBalance: results.data?.eth || 0,
    tokenBalances: results.data?.tokens || [],
    data: results.data || { eth: 0, tokens: [], address },
  }
}

type WalletBalances = {
  eth: number
  tokens: TokenBalance[]
  address?: CryptoAddress
}

type GetAllWalletBalancesProps = {
  addresses: CryptoAddress[]
  enabled?: boolean
  refetchInterval?: number
}

type GetAllWalletBalancesResponse = {
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  error: Error | null | undefined
  balances: WalletBalances[]
}

// eslint-disable-next-line max-lines-per-function, complexity
export function useGetAllWalletBalances({
  addresses = [],
  enabled = true,
  refetchInterval,
}: GetAllWalletBalancesProps): GetAllWalletBalancesResponse {
  const client = usePublicClient({ chainId: active_chain.id })
  const {
    data,
    isLoading: isLoadingContracts,
    isError: isErrorContracts,
    error: errorContracts,
  } = useListCryptoContracts()
  const contracts = data?.contracts ?? []

  const tokenContracts = contracts.filter(
    (contract) => contract.category === 'token',
  )

  const walletQueries = useQueries({
    queries: addresses.map((address) => {
      const _enabled = client != null && tokenContracts.length > 0 && enabled

      return {
        queryKey: keys.allBalances.wallet(disableKeyWhenMissing(address))
          .queryKey,
        refetchInterval,
        staleTime: 1 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        enabled: _enabled,
        queryFn: async () => {
          if (!client) return null

          // Get ETH balance
          let ethBalance = 0
          try {
            const rawEthBalance = await client.getBalance({ address })
            ethBalance = Number(formatUnits(rawEthBalance, 18)) // ETH has 18 decimals
          } catch (error) {
            console.error('Error fetching ETH balance:', error)
          }

          // Get ERC20 token balances
          const balancePromises = tokenContracts.map(async (contract) => {
            if (!contract.symbol) {
              return null
            }

            let decimals = 18
            try {
              decimals = await client.readContract({
                abi: erc20Abi,
                functionName: 'decimals',
                address: contract.address,
              })
            } catch (error) {
              console.warn(
                `Error fetching decimals for ${contract.symbol}:`,
                error,
              )
            }

            try {
              const rawBalance = await client.readContract({
                abi: erc20Abi,
                functionName: 'balanceOf',
                args: [address], // if doesn't exist, query is disabled
                address: contract.address,
              })
              const balance = amount_to_tokens(rawBalance, decimals)

              if (balance <= 0) {
                return null
              }

              return {
                symbol: contract.symbol,
                balance,
                decimals,
                token_address: contract.address,
              }
            } catch (error) {
              console.error(
                `Error fetching balance for ${contract.symbol}:`,
                error,
              )
              return null
            }
          })

          const tokenResults = await Promise.all(balancePromises)

          const tokens = tokenResults
            .filter(
              (balance): balance is TokenBalance =>
                balance !== null && balance.balance != 0,
            )
            .sort((a, b) => b.balance - a.balance)

          return {
            eth: ethBalance,
            tokens,
            address,
          }
        },
      }
    }),
  })

  const isLoading =
    isLoadingContracts || walletQueries.some((query) => query.isLoading)
  const isFetching =
    isLoadingContracts || walletQueries.some((query) => query.isFetching)
  const isError =
    isErrorContracts || walletQueries.some((query) => query.isError)
  const error =
    errorContracts || walletQueries.find((query) => query.error)?.error

  const allBalances: WalletBalances[] = walletQueries.map(
    (query, index) =>
      query.data || { eth: 0, tokens: [], address: addresses[index] },
  )

  return {
    isLoading,
    isError,
    isFetching,
    error,
    balances: allBalances,
  }
}
